import {useEffect, useRef, useState} from "react";
import {useSelector} from "react-redux";
import classNames from "classnames";
import * as Api from "utils/api/api.js";
import * as reduxSelectors from "components/store/application.reducers";
import {DETAILS_VIEW_TYPE} from "utils/constants";
import {Loader, LoaderSize} from "components/common/Loader.component";
import {Notification, NotificationType} from "components/common/Notification.component";
import {
    BNXT_EDITOR_ORIGIN,
    BNXT_EDITOR_URL,
    BNXT_LOG_PREFIX,
    BnxtEditRequest,
    buildOpenTaskMessage,
    extractApprovalToken,
    isEditorReadyMessage
} from "./bnxtOpenTask";

interface BnxtDocumentEditorProps {
    taskId: string;
    id: number | string | null;
    companyId: number | string;
    externalAppId: number | string;
    viewType: string;
    isReadOnly: boolean;
    isPopup: boolean;
    translate: (key: string) => string;
}

//const READY_TIMEOUT_MS = 15000;

interface BnxtSession {
    approvalToken: string;
    editRequest: BnxtEditRequest;
}

export function BnxtDocumentEditor(props: BnxtDocumentEditorProps) {
    const userConfiguration: any = useSelector((store) => reduxSelectors.getUsersData(store));
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const sentSessionRef = useRef<BnxtSession | null>(null);
    const [session, setSession] = useState<BnxtSession | null>(null);
    const sessionRef = useRef<BnxtSession | null>(null);
    sessionRef.current = session;
    const [editorReady, setEditorReady] = useState(false);
    const editorReadyRef = useRef(false);
    editorReadyRef.current = editorReady;
    const [sessionError, setSessionError] = useState(false);

    useEffect(() => {
        let cancelled = false;
        const isDocumentView = props.viewType === DETAILS_VIEW_TYPE.DOCUMENT;
        const getEditor = isDocumentView ? Api.getDocumentOverviewEditor : Api.getDocumentEditor;

        setSession(null);
        setSessionError(false);

        getEditor(props.taskId, props.companyId).then((response) => {
            if (cancelled) {
                return;
            }

            const approvalToken = extractApprovalToken(response?.url);

            if (!approvalToken) {
                setSessionError(true);
                return;
            }

            setSession({
                approvalToken,
                editRequest: {
                    companyId: (props.viewType === DETAILS_VIEW_TYPE.TASK ? props.companyId : userConfiguration.currentContext) + "",
                    systemId: props.externalAppId + "",
                    userId: userConfiguration.currentUserId + "",
                    readonly: props.isReadOnly,
                    administrator: userConfiguration.userRoles.systemAdministrator,
                    taskId: props.id
                }
            });
        }).catch(() => {
            if (!cancelled) {
                setSessionError(true);
            }
        });

        return () => {
            cancelled = true;
        };
    }, [props.taskId]);

    useEffect(() => {
        const onMessage = (event: MessageEvent) => {
            // skip our own app's postMessage chatter, log everything else so an unexpected origin is visible too
            if (event.origin !== window.location.origin) {
                console.log(`${BNXT_LOG_PREFIX} message`, {origin: event.origin, data: event.data});
            }

            if (event.origin !== BNXT_EDITOR_ORIGIN) {
                return;
            }

            if (isEditorReadyMessage(event.data)) {
                setEditorReady(true);
            } else {
                console.warn(`${BNXT_LOG_PREFIX} not recognised as a ready message`, event.data);
            }
        };

        window.addEventListener("message", onMessage, false);

        return () => window.removeEventListener("message", onMessage);
    }, []);

    useEffect(() => {
        if (!editorReady || !session || sentSessionRef.current === session) {
            return;
        }

        iframeRef.current?.contentWindow?.postMessage(
            buildOpenTaskMessage(session.approvalToken, session.editRequest),
            BNXT_EDITOR_ORIGIN
        );
        sentSessionRef.current = session;
    }, [editorReady, session]);

    const onIframeLoad = () => {
        console.log(`${BNXT_LOG_PREFIX} iframe loaded`, BNXT_EDITOR_URL);

        // window.setTimeout(() => {
        //     if (!sentSessionRef.current) {
        //         console.warn(`${BNXT_LOG_PREFIX} no ready message ${READY_TIMEOUT_MS}ms after load`, {
        //             editorReady: editorReadyRef.current,
        //             hasApprovalToken: sessionRef.current !== null
        //         });
        //     }
        // }, READY_TIMEOUT_MS);
    };

    const frameClasses = classNames("document-editor-frame height-100", {"document-editor-popup": props.isPopup});

    if (sessionError) {
        return (
            <div className="height-100">
                <div className={frameClasses}>
                    <div className="d-flex justify-content-center mt-32">
                        <Notification
                            type={NotificationType.ERROR}
                            title={props.translate("popUp.documentEditor.errorTitle")}
                            description={props.translate("popUp.documentEditor.errorSuggestion")}
                        />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="height-100 position-relative">
            {!editorReady &&
                <div className="position-absolute top-50 w-100">
                    <Loader size={LoaderSize.Medium}/>
                </div>
            }
            <iframe
                ref={iframeRef}
                onLoad={onIframeLoad}
                id={"Bnxt-Document-Editor-Iframe"}
                src={BNXT_EDITOR_URL}
                title={"Document Editor"}
                allow={"clipboard-read; clipboard-write"}
                className={classNames("w-100", frameClasses)}
            />
        </div>
    );
}

export default BnxtDocumentEditor;
